import {
  createContext, useContext, useEffect, useState,
} from 'react';
import Queue from 'promise-queue';
import { FetchResult, gql, useMutation } from '@apollo/client';
import { WorkflowCompletionVisual } from './workflowCompletion.visual';
import { ovAnalyticsEvents } from '../../../util/analytics/analytics';
import { AnalyticsContext } from '../../../providers/analyticsProvider';
import {
  BankAccount, Institution, SubAccount,
} from '../../../interfaces';
import { usePermissions } from '../../../providers/userContextProvider';
import { NEXT_STEP, PREV_STEP, UPDATE_WORKFLOW_COMPLETION } from './subSteps/queries';
import { FormSignerMapping } from './subSteps/manageSigners/manageSigners';

export type CreatedAccountsType = {
  id?: string
  type?: string
  state?: string
};

export interface WorkflowDataType {
  // Users
  currentUserId?: string;

  // Accounts
  currentAccountId?: string;
  currentAccountType?: string;
  currentAccountState?: string;
  createdAccounts?: CreatedAccountsType[];
  skipEditAccount?: boolean;

  // Goals
  currentGoalId?: string;
  currentGoalType?: string;
  currentGoalName?: string;
  createdGoalIds?: string[];
  goalTimeHorizons?: any;

  // Sub-Accounts
  currentSubAccountId?: string;
  createdSubAccountIds?: string[];

  // Households
  currentHouseholdClientGroupId?: string;
  linkingHouseholdId?: string;
  isHouseholdRemoved?: boolean;

  // Transfers - Deposit or Withdrawal
  type?: string;
  bankAccount?: BankAccount;
  subAccount?: Partial<SubAccount>;
  fromSubAccount?: Partial<SubAccount>;
  institution?: Partial<Institution>;
  accountNumber?: string;
  accountType?: string;
  fiscalYear?: number;
  transferType?: string;
  amountCents?: number;
  schedule?: string;
  scheduledDate?: Date;
  skipDocuments?: boolean;
  currentTransferId?: string;
  createdTransfers?: {
    id: string
  }[];
  createdScheduledTransfers?: {
    id: string
  }[];
  createdExternalTransfers?: {
    id: string,
    subAccount: { id: string, account: { id: string }, transfers: { id: string, manualProcess: { id: string } }[] }
  }[];
  createdCustodianDepositTransfers?: {
    confirmationNumber: string,
    accountId: string,
    amount: string,
    bankAccount: {
      accountNumber: string,
      institutionNumber: string,
      transitNumber: string
    },
    frequency: string,
    fund: string,
    startDate: string,
  }[];

  // Model Portfolios
  modelPortfolioDetails?: any;

  // Section Navigation
  segmentHistory?: string[];
  currentSegment?: string | null;

  // Other
  childWorkflowCompletion?: any

  // Signing
  signerMapping?: FormSignerMapping[];
  integrationFormPackId?: string;
}

export interface WorkflowContextType {
  workflowData: WorkflowDataType;
  setWorkflowData: (action: WorkflowDataType | ((prevState: WorkflowDataType) => WorkflowDataType)) => (Promise<void> | void);
  updateWorkflowContext?: () => Promise<FetchResult<any>>;
  navigateToSegment?: (segment: string) => void;
}

export const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        id
        incompleteFields
        iDCheckCompleted
        iDVerified
        inProvinceSince
        hasCheckedPolicyAndCondition
        affiliateOnly
      }
    }
  }
`;

export const WorkflowContext = createContext<WorkflowContextType>({
  workflowData: {},
  setWorkflowData: async (action) => { },
  updateWorkflowContext: async () => Promise.resolve({} as FetchResult<any>),
  navigateToSegment: () => { },
});

const workflowCompletionUpdateQueue = new Queue(1);

export const WorkflowCompletion = ({
  workflowCompletion,
  open,
  onClose,
  isInnerWorkflow,
  updateParent,
  onCloseIconClick = () => onClose(),
}: {
  workflowCompletion: any;
  open: boolean;
  onClose: () => void;
  isInnerWorkflow?: boolean;
  updateParent?: (activeCompletion?: any) => void;
  onCloseIconClick?: () => any;
}) => {
  const [workflowData, setWorkflowDataState] = useState<WorkflowDataType>(workflowCompletion?.context ?? {});
  const setWorkflowData = async (action: WorkflowDataType | ((prevState: WorkflowDataType) => WorkflowDataType)) => {
    const newWorkflowData = typeof action === 'function' ? action(workflowData) : action;
    setWorkflowDataState(newWorkflowData);
    if (newWorkflowData && Object.keys(newWorkflowData).length > 0 && workflowCompletion?.id) {
      await updateWorkflowCompletion({
        variables: {
          input: {
            workflowCompletionId: workflowCompletion.id,
            context: newWorkflowData,
          },
        },
      });
    }
  };
  const [activeWorkflowCompletion, setActiveWorkflowCompletion] = useState<any>(workflowCompletion);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { permissions } = usePermissions();

  const checkIfItIsFirstWorkflow = (activeWorkflowCompletionItem: any) => {
    if (Object.keys(activeWorkflowCompletionItem).length === 0) return;
    if (
      activeWorkflowCompletionItem.currentStep.id === activeWorkflowCompletionItem.workflow.steps[0].id
      && activeWorkflowCompletionItem.currentSubStep.id === activeWorkflowCompletionItem.workflow.steps[0].subSteps[0].id
    ) {
      sendAnalytic(ovAnalyticsEvents.workflowsCompletionStart, {
        workflowStepTitle: activeWorkflowCompletionItem.currentSubStep.options?.title,
        workflowId: activeWorkflowCompletionItem?.workflow?.id,
        workflowName: activeWorkflowCompletionItem?.workflow?.name,
        activeWorkflowCompletionId: activeWorkflowCompletionItem?.id,
        objectId: activeWorkflowCompletionItem?.objectId,
        objectType: activeWorkflowCompletionItem?.objectType,
      });
    }
  };
  const handleClose = () => {
    onClose?.();
  };

  const [updateIdCheckWithComplianceReview] = useMutation(UPDATE_USER, {
    variables: {
      input: {
        userId: workflowCompletion.objectId,
        iDCheckCompleted: true,
        complianceState: 'NEEDS_REVIEW',
        complianceIssueSource: 'User opted out of ID verification',
      },
    },
    onCompleted: (data: { updateUser: { user: { iDCheckCompleted: boolean; iDVerified: boolean } } }): void => { },
  });

  const [nextStep, { loading }] = useMutation(NEXT_STEP(permissions), {
    onCompleted: (data) => {
      // Action steps are always skipped (we don't return them). If we do, it means that it is the last substep.
      const isActionStep = data.nextSubStep.workflowCompletion.currentSubStep.type.includes('ACTION');
      const isLastSubStep = data.nextSubStep.workflowCompletion.currentSubStep.id === activeWorkflowCompletion.currentSubStep.id;

      if (isLastSubStep || isActionStep) {
        sendAnalytic(ovAnalyticsEvents.workflowsCompletionEnd, {
          workflowStepTitle: data.nextSubStep.workflowCompletion.currentSubStep.options?.title,
          workflowId: data.nextSubStep.workflowCompletion?.workflow?.id,
          workflowName: data.nextSubStep.workflowCompletion?.workflow?.name,
          activeWorkflowCompletionId: data.nextSubStep.workflowCompletion?.id,
          objectId: data.nextSubStep.workflowCompletion?.objectId,
          objectType: data.nextSubStep.workflowCompletion?.objectType,
        });
        handleClose();
        if (updateParent) {
          updateParent();
        }
      }

      if (updateParent) {
        updateParent({ ...data.nextSubStep.workflowCompletion });
      }
      setActiveWorkflowCompletion({
        ...data.nextSubStep.workflowCompletion,
        direction: 'FORWARD',
      });
    },
  });

  const [previousStep, { loading: prevLoading }] = useMutation(PREV_STEP(permissions), {
    variables: {
      input: { workflowCompletionId: workflowCompletion.id },
    },
    onCompleted: (data) => {
      setActiveWorkflowCompletion({
        ...data.previousSubStep.workflowCompletion,
        direction: 'BACKWARD',
      });
    },
  });

  const [updateWorkflowCompletionMutation] = useMutation(UPDATE_WORKFLOW_COMPLETION(permissions), {
    variables: {
      input: {
        workflowCompletionId: workflowCompletion.id,
        context: workflowData,
      },
    },
  });
  const updateWorkflowCompletion = (...args: Parameters<typeof updateWorkflowCompletionMutation>) => workflowCompletionUpdateQueue.add(
    () => updateWorkflowCompletionMutation(...args),
  );

  useEffect(() => {
    setActiveWorkflowCompletion(workflowCompletion);
    checkIfItIsFirstWorkflow(workflowCompletion);
    if (workflowCompletion.context) {
      setWorkflowData({ ...workflowCompletion.context, currentUserId: workflowCompletion.objectId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowCompletion]);

  const goNext = async (fromKey?: string) => {
    if (fromKey && typeof fromKey === 'string') {
      await nextStep({ variables: { input: { workflowCompletionId: workflowCompletion.id, fromKey } } });
    } else {
      await nextStep({ variables: { input: { workflowCompletionId: workflowCompletion.id } } });
    }
  };

  return (
    <WorkflowContext.Provider
      value={{
        workflowData,
        setWorkflowData,
        updateWorkflowContext: updateWorkflowCompletion,
      }}
    >
      <WorkflowCompletionVisual
        onCloseIconClick={onCloseIconClick}
        workflowCompletion={activeWorkflowCompletion}
        open={open}
        onClose={handleClose}
        onNext={goNext}
        previousStep={previousStep}
        loading={loading || prevLoading}
        setActiveWorkflowCompletion={setActiveWorkflowCompletion}
        isInnerWorkflow={isInnerWorkflow}
        handleIdSkip={() => updateIdCheckWithComplianceReview()}
      />
    </WorkflowContext.Provider>
  );
};
